import "../css/header.scss";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";
import SearchOverlay from "./SearchOverlay";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { formatCurrency } from "../utils/formatters";

const Icon = ({ name }) => {
  if (name === "search") {
    return (
      <svg
        className="site-header__icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="10.8" cy="10.8" r="6.8" fill="none" stroke="currentColor" />
        <path d="m16 16 5 5" fill="none" stroke="currentColor" />
      </svg>
    );
  }

  if (name === "heart") {
    return (
      <svg
        className="site-header__icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M20.8 5.7c-2.5-2.6-6.7-2.3-8.8.6-2.1-2.9-6.3-3.2-8.8-.6-2.5 2.6-2.1 6.8.6 9.2L12 22l8.2-7.1c2.7-2.4 3.1-6.6.6-9.2Z"
          fill="none"
          stroke="currentColor"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (name === "bag") {
    return (
      <svg
        className="site-header__icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M4.5 8.5h15l-1 13h-13l-1-13Z"
          fill="none"
          stroke="currentColor"
        />
        <path
          d="M8.5 9V6a3.5 3.5 0 0 1 7 0v3"
          fill="none"
          stroke="currentColor"
        />
      </svg>
    );
  }

  return (
    <svg
      className="site-header__icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 6.5h18M3 12h18M3 17.5h18" fill="none" stroke="currentColor" />
    </svg>
  );
};

const getProduct = (id) =>
  productData.find((product) => String(product.id) === String(id));

const keepFocusInside = (event, container) => {
  if (event.key !== "Tab" || !container) return;

  const focusableElements = Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
  if (!focusableElements.length) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
};

function Header() {
  const {
    cartItems,
    cartCount,
    cartSubtotal,
    wishlistCount,
    removeFromCart,
    isCartOpen,
    openCart,
    closeCart,
  } = useContext(CartContext);
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const menuCloseRef = useRef(null);
  const menuPanelRef = useRef(null);
  const searchButtonRef = useRef(null);
  const bagButtonRef = useRef(null);
  const cartCloseRef = useRef(null);
  const cartPanelRef = useRef(null);

  const currentUrl = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search]
  );

  const closeMenu = useCallback((restoreFocus = true) => {
    setIsMenuOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => menuButtonRef.current?.focus());
    }
  }, []);

  const closeSearch = useCallback((restoreFocus = true) => {
    setIsSearchOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => searchButtonRef.current?.focus());
    }
  }, []);

  const closeMiniCart = useCallback(
    (restoreFocus = true) => {
      closeCart();
      if (restoreFocus) {
        window.requestAnimationFrame(() => bagButtonRef.current?.focus());
      }
    },
    [closeCart]
  );

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    closeCart();
  }, [closeCart, location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (isMenuOpen) menuCloseRef.current?.focus();
  }, [isMenuOpen]);

  useEffect(() => {
    if (isCartOpen) cartCloseRef.current?.focus();
  }, [isCartOpen]);

  useEffect(() => {
    if (!isMenuOpen && !isSearchOpen && !isCartOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isCartOpen, isMenuOpen, isSearchOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (isMenuOpen) closeMenu();
        if (isCartOpen) closeMiniCart();
        return;
      }

      if (isMenuOpen) keepFocusInside(event, menuPanelRef.current);
      if (isCartOpen) keepFocusInside(event, cartPanelRef.current);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeMenu, closeMiniCart, isCartOpen, isMenuOpen]);

  const openMenu = () => {
    setIsSearchOpen(false);
    closeCart();
    setIsMenuOpen(true);
  };

  const openSearch = () => {
    setIsMenuOpen(false);
    closeCart();
    setIsSearchOpen(true);
  };

  const handleBagClick = () => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    if (isCartOpen) {
      closeMiniCart();
    } else {
      openCart();
    }
  };

  const renderNavigationLink = (item, mobile = false) => {
    const baseClass = mobile ? "mobile-menu__link" : "site-navigation__link";
    const activeClass = mobile
      ? "mobile-menu__link--active"
      : "site-navigation__link--active";

    if (item.href.includes("?")) {
      return (
        <Link
          className={`${baseClass}${
            currentUrl === item.href ? ` ${activeClass}` : ""
          }`}
          to={item.href}
          aria-current={currentUrl === item.href ? "page" : undefined}
        >
          {item.label}
        </Link>
      );
    }

    return (
      <NavLink
        className={({ isActive }) =>
          `${baseClass}${isActive ? ` ${activeClass}` : ""}`
        }
        to={item.href}
      >
        {item.label}
      </NavLink>
    );
  };

  return (
    <>
      <header className="site-header">
        <div className="announcement-bar">
          <p className="announcement-bar__text">{siteConfig.announcement}</p>
        </div>

        <div className="site-header__inner">
          <button
            ref={menuButtonRef}
            className="site-header__menu-button"
            type="button"
            onClick={openMenu}
            aria-label="Open navigation"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
          >
            <Icon name="menu" />
          </button>

          <Link
            className="site-header__logo"
            to="/"
            aria-label={siteConfig.logo.alt}
          >
            {siteConfig.logo.image ? (
              <img
                className="site-header__logo-image"
                src={siteConfig.logo.image}
                alt={siteConfig.logo.alt}
              />
            ) : (
              <span className="site-header__wordmark">
                {siteConfig.logo.text}
              </span>
            )}
          </Link>

          <nav className="site-navigation" aria-label="Primary navigation">
            <ul className="site-navigation__list">
              {siteConfig.navigation.map((item) => (
                <li className="site-navigation__item" key={item.href}>
                  {renderNavigationLink(item)}
                </li>
              ))}
              <li className="site-navigation__item site-navigation__more">
                <button
                  className="site-navigation__link site-navigation__more-button"
                  type="button"
                  onClick={() => setIsMoreOpen((open) => !open)}
                  aria-expanded={isMoreOpen}
                >
                  More
                </button>
                {isMoreOpen && (
                  <div className="site-navigation__more-menu">
                    {siteConfig.moreNavigation.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsMoreOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </li>
            </ul>
          </nav>

          <div className="site-header__actions">
            <button
              ref={searchButtonRef}
              className="site-header__action"
              type="button"
              onClick={openSearch}
              aria-label="Search"
              aria-expanded={isSearchOpen}
            >
              <Icon name="search" />
              <span className="site-header__action-label">Search</span>
            </button>

            <Link
              className="site-header__action"
              to="/shop?wishlist=true"
              aria-label={`Favourites, ${wishlistCount} saved`}
            >
              <Icon name="heart" />
              <span className="site-header__action-label">Favourites</span>
              {wishlistCount > 0 && (
                <span className="site-header__count" aria-hidden="true">
                  {wishlistCount}
                </span>
              )}
            </Link>

            <button
              ref={bagButtonRef}
              className="site-header__action"
              type="button"
              onClick={handleBagClick}
              aria-label={`Shopping bag, ${cartCount} ${
                cartCount === 1 ? "item" : "items"
              }`}
              aria-expanded={isCartOpen}
              aria-controls="mini-cart"
            >
              <Icon name="bag" />
              <span className="site-header__action-label">Bag</span>
              <span className="site-header__count" aria-hidden="true">
                {cartCount}
              </span>
            </button>
          </div>
        </div>
      </header>

      {isMenuOpen && (
        <div className="mobile-menu">
          <button
            className="mobile-menu__backdrop"
            type="button"
            onClick={() => closeMenu()}
            aria-label="Close navigation"
            tabIndex="-1"
          />
          <aside
            id="mobile-navigation"
            className="mobile-menu__panel"
            ref={menuPanelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-menu-title"
          >
            <div className="mobile-menu__header">
              <span id="mobile-menu-title" className="mobile-menu__wordmark">
                {siteConfig.logo.text}
              </span>
              <button
                ref={menuCloseRef}
                className="mobile-menu__close"
                type="button"
                onClick={() => closeMenu()}
                aria-label="Close navigation"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <nav
              className="mobile-menu__nav"
              aria-label="Mobile primary navigation"
            >
              <ul className="mobile-menu__list">
                {siteConfig.navigation.map((item) => (
                  <li className="mobile-menu__item" key={item.href}>
                    {renderNavigationLink(item, true)}
                  </li>
                ))}
                {siteConfig.moreNavigation.map((item) => (
                  <li className="mobile-menu__item" key={item.href}>
                    {renderNavigationLink(item, true)}
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mobile-menu__footer">
              <Link className="mobile-menu__utility-link" to="/contact">
                Contact
              </Link>
              <Link className="mobile-menu__utility-link" to="/shipping">
                Shipping &amp; delivery
              </Link>
            </div>
          </aside>
        </div>
      )}

      <SearchOverlay isOpen={isSearchOpen} onClose={closeSearch} />

      {isCartOpen && (
        <div className="mini-cart">
          <button
            className="mini-cart__backdrop"
            type="button"
            onClick={() => closeMiniCart()}
            aria-label="Close shopping bag"
            tabIndex="-1"
          />
          <aside
            id="mini-cart"
            className="mini-cart__panel"
            ref={cartPanelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mini-cart-title"
          >
            <div className="mini-cart__header">
              <h2 id="mini-cart-title" className="mini-cart__title">
                Your bag <span>({cartCount})</span>
              </h2>
              <button
                ref={cartCloseRef}
                className="mini-cart__close"
                type="button"
                onClick={() => closeMiniCart()}
                aria-label="Close shopping bag"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            {cartItems.length === 0 ? (
              <div className="mini-cart__empty">
                <p>Your bag is waiting for something made to last.</p>
                <Link className="mini-cart__shop-link" to="/shop">
                  Explore the collection
                </Link>
              </div>
            ) : (
              <>
                <ul className="mini-cart__items">
                  {cartItems.map((item) => {
                    const product = getProduct(item.id);
                    if (!product) return null;

                    return (
                      <li
                        className="mini-cart__item"
                        key={`${item.id}-${item.size || ""}-${
                          item.color || ""
                        }`}
                      >
                        <Link
                          className="mini-cart__image-link"
                          to={`/shopDetails/${product.id}`}
                          aria-label={`View ${product.name}`}
                        >
                          <img
                            className="mini-cart__image"
                            src={product.image}
                            alt=""
                          />
                        </Link>
                        <div className="mini-cart__item-copy">
                          <Link
                            className="mini-cart__product-name"
                            to={`/shopDetails/${product.id}`}
                          >
                            {product.name}
                          </Link>
                          <p className="mini-cart__variant">
                            {[item.color, item.size]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                          <p className="mini-cart__quantity">
                            Qty {item.quantity} ·{" "}
                            {formatCurrency(product.price * item.quantity)}
                          </p>
                          <button
                            className="mini-cart__remove"
                            type="button"
                            onClick={() =>
                              removeFromCart(item.id, item.size, item.color)
                            }
                            aria-label={`Remove ${product.name} from bag`}
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="mini-cart__footer">
                  <div className="mini-cart__subtotal">
                    <span>Subtotal</span>
                    <strong>{formatCurrency(cartSubtotal)}</strong>
                  </div>
                  <p className="mini-cart__shipping-note">
                    Taxes included. Shipping calculated at checkout.
                  </p>
                  <Link className="mini-cart__checkout" to="/checkout">
                    Checkout
                  </Link>
                  <Link className="mini-cart__view-bag" to="/cartpage">
                    View bag
                  </Link>
                </div>
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}

export default Header;
